import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Database, 
  Server, 
  BarChart3, 
  MessageSquare, 
  Mail, 
  Instagram, 
  Image, 
  Cookie, 
  Shield, 
  Lock,
  Phone,
  MapPin
} from 'lucide-react';
import SEOHead from './seo/SEOHead';
import { BreadcrumbSchema } from './seo/SchemaMarkup';
import { PAGE_SEO, SITE_URL } from '../seoConfig';

const Datenschutz = () => {
  const breadcrumbs = [
    { name: "Start", url: SITE_URL },
    { name: "Datenschutz", url: `${SITE_URL}/datenschutz` }
  ];

  return (
    <>
      <SEOHead 
        title={PAGE_SEO.datenschutz.title}
        description={PAGE_SEO.datenschutz.description}
        canonical={PAGE_SEO.datenschutz.canonical}
      />
      <BreadcrumbSchema items={breadcrumbs} />
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-[#DC2626] transition-colors duration-300"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Zurück zur Startseite</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="datenschutz-title">
              Datenschutzerklärung
            </h1>
            <div className="w-24 h-1 bg-[#DC2626] mx-auto rounded-full"></div>
          </div>

          {/* Content Cards */}
          <div className="space-y-8">
            
            {/* 1. Verantwortlicher */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="responsible-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">1. Verantwortlicher</h2>
              </div>
              
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p className="text-lg font-semibold text-gray-900">Haxhi Kyreziu – HK Altbausanierung</p>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-[#DC2626] flex-shrink-0 mt-1" />
                  <span>Gartenstraße 16, 73061 Ebersbach an der Fils, Deutschland</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-[#DC2626] flex-shrink-0" />
                  <a href="tel:01729308274" className="hover:text-[#DC2626] transition-colors">0172 9308274</a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#DC2626] flex-shrink-0" />
                  <a href="mailto:hk-altbausanierung@outlook.de" className="hover:text-[#DC2626] transition-colors">hk-altbausanierung@outlook.de</a>
                </div>
              </div>
            </div>

            {/* 2. Allgemeine Hinweise */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="general-info-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Database className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">2. Allgemeine Hinweise zur Datenverarbeitung</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und Leistungen erforderlich ist.</p>
                
                <div className="pt-4 border-t border-gray-100">
                  <p className="font-semibold mb-3">Rechtsgrundlagen (je nach Fall):</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen / Vertrag)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse, z. B. sichere und stabile Bereitstellung der Website)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), sofern wir in Einzelfällen Einwilligungen einholen</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 3. Hosting */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="hosting-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Server className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">3. Hosting der Website</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Unsere Website wird bei Vercel gehostet. Wir nutzen die Region Frankfurt (Deutschland) für die Ausführung/Bereitstellung unserer Anwendung. Gleichzeitig nutzt Vercel ein globales Netzwerk (CDN/Points of Presence), um die Website performant auszuliefern.</p>
                
                <div className="pt-4 border-t border-gray-100">
                  <p className="font-semibold mb-3">Welche Daten werden dabei verarbeitet?</p>
                  <p className="mb-2">Beim Aufruf der Website werden durch Hosting/Server-Logs typischerweise verarbeitet:</p>
                  <ul className="space-y-1 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>IP-Adresse (in der Regel in Logfiles)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>Datum und Uhrzeit der Anfrage</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>aufgerufene Seite/URL</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>Referrer-URL</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>Browsertyp/-version, Betriebssystem (User-Agent)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>Statuscodes, übertragene Datenmenge</span>
                    </li>
                  </ul>
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-2">
                  <p><span className="font-semibold">Zwecke:</span> Auslieferung der Website, IT-Sicherheit, Fehleranalyse, Missbrauchs-/Angriffserkennung.</p>
                  <p><span className="font-semibold">Rechtsgrundlage:</span> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherem, stabilem Betrieb).</p>
                  <p><span className="font-semibold">Auftragsverarbeitung:</span> Wir haben mit Vercel einen Auftragsverarbeitungsvertrag (DPA) abgeschlossen.</p>
                  <p><span className="font-semibold">Drittlandtransfer / USA:</span> Vercel ist ein US-Anbieter; je nach technischer Ausgestaltung kann eine Verarbeitung/Übermittlung in Drittländer (insb. USA) nicht ausgeschlossen werden. Wir stützen uns dabei auf geeignete Garantien, insbesondere Standardvertragsklauseln/Regelungen im Rahmen des DPA.</p>
                </div>
              </div>
            </div>

            {/* 4. Webanalyse */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="analytics-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">4. Webanalyse (Vercel Analytics)</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Wir nutzen Vercel Analytics zur statistischen Auswertung der Nutzung unserer Website. Dabei werden nach Angaben von Vercel keine Third-Party-Cookies eingesetzt; Besucher werden über einen aus der Anfrage abgeleiteten Hash identifiziert, und Sitzungsinformationen werden nicht dauerhaft gespeichert (z. B. automatische Verwerfung nach 24 Stunden).</p>
                
                <div className="pt-4 border-t border-gray-100">
                  <p className="font-semibold mb-2">Verarbeitete Daten (typisch):</p>
                  <ul className="space-y-1 ml-4 mb-4">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>aufgerufene Seiten/Events, ungefähre technische Metadaten</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span>gekürzte/abgeleitete Identifikatoren (Hash aus Request-Daten)</span>
                    </li>
                  </ul>
                  <p><span className="font-semibold">Zweck:</span> Reichweitenmessung, Verbesserung der Website.</p>
                  <p><span className="font-semibold">Rechtsgrundlage:</span> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer bedarfsgerechten, optimierten Website).</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <p className="text-sm text-gray-600"><span className="font-semibold">Hinweis:</span> Da hierfür keine Third-Party-Cookies eingesetzt werden, ist regelmäßig kein Cookie-Banner allein wegen dieser Statistik erforderlich (sofern keine weiteren Tracking-Tools genutzt werden).</p>
                </div>
              </div>
            </div>

            {/* 5. Kontaktformular */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="contact-form-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">5. Kontaktformular und Kontaktaufnahme per E-Mail/Telefon</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Wenn Sie uns über das Kontaktformular kontaktieren, verarbeiten wir die von Ihnen eingegebenen Daten:</p>
                <ul className="space-y-1 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Name</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>E-Mail-Adresse</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Telefonnummer</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Nachricht</span>
                  </li>
                </ul>

                <div className="pt-4 border-t border-gray-100 space-y-3">
                  <p><span className="font-semibold">Pflichtfelder:</span> alle genannten Felder sind erforderlich, damit wir Ihr Anliegen bearbeiten und Sie kontaktieren können.</p>
                  <p><span className="font-semibold">Übermittlung/Empfang:</span> Die Daten werden ausschließlich per E-Mail an unser Postfach (Outlook) übermittelt (SMTP-Versand). Es erfolgt keine zusätzliche Speicherung in einer Datenbank/CRM.</p>
                  <p><span className="font-semibold">Zweck:</span> Bearbeitung der Anfrage, Anbahnung/Abwicklung von Aufträgen.</p>
                  <p><span className="font-semibold">Rechtsgrundlage:</span> Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen/Vertrag); ggf. Art. 6 Abs. 1 lit. f DSGVO (allgemeine Kommunikation, Organisation).</p>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="font-semibold mb-2">Speicherdauer:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span><span className="font-medium">Kommt kein Auftrag zustande:</span> Wir löschen/anonymisieren die Kommunikation, sobald die Bearbeitung abgeschlossen ist und keine Aufbewahrungspflichten entgegenstehen.</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#DC2626] font-bold">•</span>
                      <span><span className="font-medium">Kommt ein Auftrag zustande:</span> Wir speichern die Kommunikation im Rahmen der gesetzlichen Aufbewahrungsfristen (z. B. handels-/steuerrechtlich, soweit einschlägig) und solange dies zur Vertragsdurchführung bzw. zur Abwehr/Durchsetzung von Ansprüchen erforderlich ist.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 6. E-Mail-Dienst */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="email-service-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">6. E-Mail-Dienst (Outlook)</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Für den Empfang und die Bearbeitung von E-Mails nutzen wir Dienste von Microsoft (Outlook). Dabei können die oben genannten Kommunikationsdaten an Microsoft übermittelt und dort verarbeitet werden.</p>
                <p><span className="font-semibold">Zweck:</span> zuverlässige Zustellung und Bearbeitung von Nachrichten.</p>
                <p><span className="font-semibold">Rechtsgrundlage:</span> Art. 6 Abs. 1 lit. b DSGVO / Art. 6 Abs. 1 lit. f DSGVO (je nach Anfrage).</p>
                
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <p className="text-sm text-gray-600"><span className="font-semibold">Hinweis:</span> Je nach technischer Ausgestaltung kann eine Verarbeitung in Drittländern nicht ausgeschlossen werden. Maßgeblich sind die jeweiligen Datenschutz-/Vertragsbedingungen des Anbieters.</p>
                </div>
              </div>
            </div>

            {/* 7. Social Media */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="social-media-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Instagram className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">7. Social-Media-Link zu Instagram</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Auf unserer Website befindet sich ein Link zu unserem Profil bei Instagram. Es handelt sich nur um einen normalen Link (kein eingebettetes Plugin, kein Pixel).</p>
                <p>Wenn Sie den Link anklicken, werden Sie auf die Seiten von Instagram weitergeleitet. Dabei können durch Instagram personenbezogene Daten (z. B. IP-Adresse, Referrer) verarbeitet werden. Die Verarbeitung erfolgt dann in eigener Verantwortung von Instagram.</p>
                <p><span className="font-semibold">Rechtsgrundlage für das Setzen des Links:</span> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Außendarstellung und Kommunikation).</p>
              </div>
            </div>

            {/* 8. Bilder/Medien */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="media-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Image className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">8. Bilder/Medien (lokale Einbindung)</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Wir verwenden eigene bzw. heruntergeladene Bilder (z. B. von Unsplash), die lokal in unser Projekt eingebunden werden. Es findet dadurch kein automatischer Datenaustausch mit Unsplash beim Seitenaufruf statt.</p>
              </div>
            </div>

            {/* 9. Cookies */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="cookies-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">9. Cookies</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Wir setzen keine Marketing- oder Tracking-Cookies ein. Vercel Analytics arbeitet nach Anbieterangaben ohne Third-Party-Cookies.</p>
                <p>Technisch notwendige Daten können im Rahmen des Hostings/der Bereitstellung verarbeitet werden (siehe Hosting/Logs).</p>
              </div>
            </div>

            {/* 10. Ihre Rechte */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="rights-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">10. Ihre Rechte</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Sie haben – soweit die gesetzlichen Voraussetzungen vorliegen – folgende Rechte:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Auskunft (Art. 15 DSGVO)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Berichtigung (Art. 16 DSGVO)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Löschung (Art. 17 DSGVO)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Einschränkung der Verarbeitung (Art. 18 DSGVO)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Datenübertragbarkeit (Art. 20 DSGVO)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-[#DC2626] font-bold">•</span>
                    <span>Widerspruch gegen Verarbeitungen auf Basis von Art. 6 Abs. 1 lit. f DSGVO (Art. 21 DSGVO)</span>
                  </li>
                </ul>
                <p className="pt-2">Außerdem haben Sie das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren (Art. 77 DSGVO).</p>
              </div>
            </div>

            {/* 11. Datensicherheit */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100" data-testid="security-card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#DC2626]/10 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-[#DC2626]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">11. Datensicherheit</h2>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Wir treffen angemessene technische und organisatorische Maßnahmen, um Ihre Daten vor Verlust, Manipulation und unberechtigtem Zugriff zu schützen.</p>
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <p className="text-sm text-gray-600"><span className="font-semibold">Hinweis:</span> Details dazu ergeben sich u. a. aus den Sicherheits-/Compliance-Angaben des Hosting-Anbieters.</p>
                </div>
              </div>
            </div>

          </div>

          {/* Back Button */}
          <div className="text-center mt-12">
            <Link 
              to="/" 
              className="inline-flex items-center space-x-2 px-8 py-4 bg-[#DC2626] text-white rounded-full font-semibold hover:bg-[#B91C1C] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              data-testid="back-to-home-button"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Zurück zur Startseite</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Datenschutz;
